'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';
import {
  DollarSign,
  Target,
  Briefcase,
  MessageSquare,
  Award,
  Phone,
  Mail,
  Users,
  Gift,
  Palmtree,
  Home,
  BadgeCheck,
  TrendingUp,
  GraduationCap,
  ChevronDown,
} from 'lucide-react';

type Scenario = 'new-offer' | 'counter-offer' | 'raise' | 'promotion';
type Method = 'phone' | 'email' | 'in-person';

interface ScriptData {
  opening: string;
  theAsk: string;
  justification: string;
  thePause: string;
  pushbacks: { objection: string; response: string }[];
  alternatives: { icon: string; label: string; suggestion: string }[];
  fullText: string;
}

const scenarioLabels: Record<Scenario, string> = {
  'new-offer': 'New Job Offer',
  'counter-offer': 'Counter Offer',
  raise: 'Asking for a Raise',
  promotion: 'Promotion Discussion',
};

const methodLabels: Record<Method, string> = {
  phone: 'Phone Call',
  email: 'Email',
  'in-person': 'In-Person Meeting',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function generateScript(
  currentSalary: number,
  targetSalary: number,
  roleTitle: string,
  scenario: Scenario,
  leverage: string,
  method: Method
): ScriptData {
  const gap = targetSalary - currentSalary;
  const gapPercent = Math.round((gap / currentSalary) * 100);
  const midpoint = Math.round((currentSalary + targetSalary) / 2);
  const isLargeGap = gapPercent > 20;
  const leveragePoints = leverage.trim()
    ? leverage
        .split(',')
        .map((l) => l.trim())
        .filter(Boolean)
    : [];
  const hasLeverage = leveragePoints.length > 0;

  const isEmail = method === 'email';
  const greeting = isEmail ? `Dear Hiring Manager,\n\n` : '';
  const signOff = isEmail
    ? `\n\nThank you for your time and consideration.\n\nBest regards,\n[Your Name]`
    : '';

  // --- Opening ---
  let opening = '';
  if (scenario === 'new-offer') {
    if (isEmail) {
      opening = `${greeting}Thank you so much for extending the offer for the ${roleTitle} position. I'm genuinely excited about the opportunity to join the team and contribute to the company's goals. After carefully reviewing the full compensation package, I'd like to discuss the base salary.`;
    } else {
      opening = `"Thank you so much for the offer for the ${roleTitle} role. I'm really excited about this opportunity and I can see myself making a strong impact on the team. I've had a chance to review the full package, and I'd love to discuss the base salary with you."`;
    }
  } else if (scenario === 'counter-offer') {
    if (isEmail) {
      opening = `${greeting}Thank you for getting back to me with the updated offer for the ${roleTitle} position. I appreciate the team's willingness to continue this conversation. I've given the revised numbers careful thought, and I'd like to share my perspective.`;
    } else {
      opening = `"Thank you for coming back with the updated numbers. I really appreciate the willingness to work together on this. I've given this a lot of thought, and I'd like to share where I'm coming from."`;
    }
  } else if (scenario === 'raise') {
    if (isEmail) {
      opening = `${greeting}Thank you for taking the time to discuss my compensation. I've truly enjoyed my time in the ${roleTitle} role and I'm proud of what I've accomplished. I'd like to have an open conversation about aligning my salary with the value I've been delivering.`;
    } else {
      opening = `"Thank you for making time to meet with me. I really value my role here as ${roleTitle}, and I'm proud of what I've been able to contribute. I'd like to have an open conversation about my compensation."`;
    }
  } else {
    if (isEmail) {
      opening = `${greeting}Thank you for discussing the ${roleTitle} promotion opportunity with me. I'm excited about taking on greater responsibility and continuing to grow with the company. As part of this transition, I'd like to discuss the compensation that would come with this expanded role.`;
    } else {
      opening = `"Thank you for considering me for the ${roleTitle} promotion. I'm excited about the chance to take on more responsibility. As we discuss this transition, I'd like to talk about the compensation that reflects the expanded scope of this role."`;
    }
  }

  // --- The Ask ---
  let theAsk = '';
  const targetFormatted = formatCurrency(targetSalary);
  const currentFormatted = formatCurrency(currentSalary);

  if (scenario === 'new-offer' || scenario === 'counter-offer') {
    if (isEmail) {
      theAsk = `Based on my research into market rates for ${roleTitle} positions, along with the experience and skills I bring to the table, I'd like to propose a base salary of ${targetFormatted}.${
        isLargeGap
          ? ` I understand this represents a meaningful increase from the initial offer of ${currentFormatted}, and I'm confident the value I'll deliver justifies this investment.`
          : ` I believe this figure accurately reflects both the market rate and the value I'll bring to the role.`
      }`;
    } else {
      theAsk = `"Based on my research into market rates for ${roleTitle} roles, and considering the skills and experience I bring, I was hoping we could land at ${targetFormatted}."${
        isLargeGap
          ? ` (Note: This is a ${gapPercent}% increase from the offered ${currentFormatted}. If they hesitate, you can say: "I'd be open to discussing how we can bridge that gap together — perhaps meeting at ${formatCurrency(midpoint)} with a performance review in six months.")`
          : ''
      }`;
    }
  } else if (scenario === 'raise') {
    if (isEmail) {
      theAsk = `Given my contributions and the current market rate for ${roleTitle} professionals, I'd like to request an adjustment to ${targetFormatted}. This reflects a ${gapPercent}% increase from my current salary of ${currentFormatted}, which I believe is well-supported by the results I've delivered.`;
    } else {
      theAsk = `"Given my contributions over the past year and the current market rate for ${roleTitle} professionals, I'd like to discuss moving my salary to ${targetFormatted}. That's a ${gapPercent}% increase from my current ${currentFormatted}, and I believe the results I've delivered support that."`;
    }
  } else {
    if (isEmail) {
      theAsk = `For the ${roleTitle} role with its expanded responsibilities, I'd like to propose a salary of ${targetFormatted}. This reflects the increased scope, leadership expectations, and market benchmarks for this level of position.`;
    } else {
      theAsk = `"For the ${roleTitle} role, given the expanded scope and responsibilities, I'd like to propose a salary of ${targetFormatted}. I've looked at benchmarks for this level and I'm confident this is in line with the market."`;
    }
  }

  // --- Justification ---
  let justificationPoints: string[] = [];

  justificationPoints.push(
    `Market research shows that ${roleTitle} professionals in similar roles earn between ${formatCurrency(
      Math.round(targetSalary * 0.9)
    )} and ${formatCurrency(Math.round(targetSalary * 1.1))} in the current market`
  );

  if (scenario === 'raise' || scenario === 'promotion') {
    justificationPoints.push(
      `My track record in this role demonstrates consistent delivery beyond expectations`
    );
    justificationPoints.push(
      `I've taken on responsibilities beyond my original job description`
    );
  } else {
    justificationPoints.push(
      `I bring specialized experience that will allow me to contribute from day one`
    );
    justificationPoints.push(
      `My background positions me to deliver measurable results in this role`
    );
  }

  if (hasLeverage) {
    leveragePoints.forEach((point) => {
      justificationPoints.push(point);
    });
  }

  let justification = '';
  if (isEmail) {
    justification = `To provide context for this request, I'd like to highlight several key points:\n\n${justificationPoints
      .map((p, i) => `${i + 1}. ${p}`)
      .join('\n')}`;
  } else {
    justification = `Here are your key talking points to justify your ask:\n\n${justificationPoints
      .map((p) => `  \u2022 "${p}"`)
      .join('\n')}\n\nDeliver these one at a time, pausing briefly between each. Confidence is key — state each point as a fact, not a question.`;
  }

  // --- The Pause ---
  let thePause = '';
  if (isEmail) {
    thePause = `Close the email after stating your case. Do not weaken your position by adding qualifiers like "but I'm flexible" or "let me know what you think is fair." Simply state your ask, provide your justification, and end with a professional sign-off. Let them come back to you.${signOff}`;
  } else {
    thePause = `After you state your number and justification, STOP TALKING. This is the most important part of the negotiation. Silence creates space for the other person to respond. Most people feel uncomfortable with silence and rush to fill it with concessions — don't be that person. Take a breath, maintain eye contact${
      method === 'phone' ? ' (or stay calmly quiet on the phone)' : ''
    }, and wait. The next person to speak usually makes a concession.`;
  }

  // --- Pushbacks ---
  const pushbacks = [
    {
      objection: '"We can\'t go that high"',
      response: isEmail
        ? `Thank you for your transparency. I understand there may be constraints. Would it be possible to explore a salary of ${formatCurrency(
            midpoint
          )} with a structured performance review at the 6-month mark, with a clear path to reach ${targetFormatted} based on defined milestones?`
        : `"I understand there are budget considerations. What if we met at ${formatCurrency(
            midpoint
          )}? I'd also be open to a performance review at six months with a clear path to reach ${targetFormatted} based on hitting specific goals we agree on now."`,
    },
    {
      objection: '"That\'s above our budget"',
      response: isEmail
        ? `I appreciate you sharing that. I'm committed to making this work. Could we explore a base closer to ${formatCurrency(
            midpoint
          )}, supplemented by a signing bonus, additional equity, or an accelerated review timeline? I'm open to creative solutions that work for both sides.`
        : `"I appreciate you sharing that. I'm really committed to making this work. Could we look at a base closer to ${formatCurrency(
            midpoint
          )} and make up the difference with a signing bonus or accelerated review cycle? I'm flexible on the structure."`,
    },
    {
      objection: '"We need to check with leadership"',
      response: isEmail
        ? `Absolutely, I understand. I'd like to ensure leadership has the full picture of the value I'd bring. Would it be helpful if I provided a brief summary of my key qualifications and market data to support the discussion? I'm happy to follow up by [specific date].`
        : `"Of course, I completely understand. Is there anything I can provide — like market data or a summary of my qualifications — that would help make the case to leadership? And when would be a good time to reconnect? I'd love to keep the momentum going."`,
    },
    {
      objection: '"What if we offer [middle ground] instead?"',
      response: isEmail
        ? `Thank you for working toward a compromise. I'd be open to a middle ground if we can also discuss [choose one: a signing bonus, additional PTO, an equity package, or a guaranteed review in 6 months]. This would help me feel confident about the total value of the package.`
        : `"I appreciate you working with me on this. I could consider that if we can also look at [pick one: a signing bonus, extra PTO, equity, or a guaranteed six-month review]. That way the total package feels right for both of us."`,
    },
    {
      objection: '"The salary is firm"',
      response: isEmail
        ? `I understand the base salary may be fixed. In that case, I'd like to explore other components of the compensation package. Specifically, I'd be interested in discussing a signing bonus, additional equity or RSUs, extra PTO days, a remote or hybrid work arrangement, or a professional development budget. Would any of these be possible?`
        : `"I understand. If the base salary is set, could we look at other parts of the package? A signing bonus, additional equity, more PTO, flexible work arrangements, or a professional development stipend would all be meaningful to me. Is there flexibility in any of those areas?"`,
    },
  ];

  // --- Alternatives ---
  const alternatives = [
    {
      icon: 'gift',
      label: 'Signing Bonus',
      suggestion: `Ask for a one-time signing bonus of ${formatCurrency(
        Math.round(gap * 0.5)
      )} to ${formatCurrency(gap)} to bridge the gap between the offer and your target.`,
    },
    {
      icon: 'palmtree',
      label: 'Additional PTO',
      suggestion: `Request 5-10 additional PTO days per year. This has real monetary value (${formatCurrency(
        Math.round((targetSalary / 260) * 5)
      )}-${formatCurrency(Math.round((targetSalary / 260) * 10))} based on your target daily rate) with minimal cost perception to the employer.`,
    },
    {
      icon: 'home',
      label: 'Remote Work Flexibility',
      suggestion: `Negotiate for remote or hybrid work arrangements. This saves commuting costs and time, and is often easier for companies to approve than salary increases.`,
    },
    {
      icon: 'badge',
      label: 'Title Upgrade',
      suggestion: `Request a more senior title (e.g., "Senior ${roleTitle}" or "${roleTitle} Lead"). A stronger title improves your future earning potential and market value.`,
    },
    {
      icon: 'trending',
      label: 'Equity / Stock Options',
      suggestion: `Ask about equity, RSUs, or stock options. Even a small equity grant can be worth significantly more than the salary gap of ${formatCurrency(
        gap
      )} over time.`,
    },
    {
      icon: 'graduation',
      label: 'Professional Development',
      suggestion: `Request a professional development budget of ${formatCurrency(
        2000
      )}-${formatCurrency(5000)} per year for conferences, courses, and certifications. Employers often see this as an investment rather than a cost.`,
    },
  ];

  // --- Full text for copy ---
  const divider = '\n' + '='.repeat(50) + '\n\n';
  let fullText = '';
  fullText += `SALARY NEGOTIATION SCRIPT\n`;
  fullText += `Role: ${roleTitle}\n`;
  fullText += `Current/Offered: ${currentFormatted} | Target: ${targetFormatted}\n`;
  fullText += `Scenario: ${scenarioLabels[scenario]} | Method: ${methodLabels[method]}\n`;
  fullText += divider;
  fullText += `STEP 1: OPENING\n\n${opening}\n`;
  fullText += divider;
  fullText += `STEP 2: THE ASK\n\n${theAsk}\n`;
  fullText += divider;
  fullText += `STEP 3: JUSTIFICATION\n\n${justification}\n`;
  fullText += divider;
  fullText += `STEP 4: THE PAUSE\n\n${thePause}\n`;
  fullText += divider;
  fullText += `PUSHBACK RESPONSES\n\n`;
  pushbacks.forEach((p) => {
    fullText += `If they say: ${p.objection}\n`;
    fullText += `Your response: ${p.response}\n\n`;
  });
  fullText += divider;
  fullText += `ALTERNATIVE NEGOTIATION POINTS\n\n`;
  alternatives.forEach((a) => {
    fullText += `${a.label}: ${a.suggestion}\n\n`;
  });

  return {
    opening,
    theAsk,
    justification,
    thePause,
    pushbacks,
    alternatives,
    fullText,
  };
}

const alternativeIcons: Record<string, React.ReactNode> = {
  gift: <Gift className="w-5 h-5 text-blue-600" />,
  palmtree: <Palmtree className="w-5 h-5 text-green-600" />,
  home: <Home className="w-5 h-5 text-purple-600" />,
  badge: <BadgeCheck className="w-5 h-5 text-amber-600" />,
  trending: <TrendingUp className="w-5 h-5 text-rose-600" />,
  graduation: <GraduationCap className="w-5 h-5 text-teal-600" />,
};

export default function NegotiationScriptContent() {
  const [currentSalary, setCurrentSalary] = useState('');
  const [targetSalary, setTargetSalary] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [scenario, setScenario] = useState<Scenario>('new-offer');
  const [leverage, setLeverage] = useState('');
  const [method, setMethod] = useState<Method>('phone');
  const [script, setScript] = useState<ScriptData | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const current = parseFloat(currentSalary.replace(/,/g, ''));
    const target = parseFloat(targetSalary.replace(/,/g, ''));

    if (!currentSalary || isNaN(current) || current <= 0) {
      newErrors.currentSalary = 'Please enter a valid salary amount';
    }
    if (!targetSalary || isNaN(target) || target <= 0) {
      newErrors.targetSalary = 'Please enter a valid target salary';
    }
    if (current > 0 && target > 0 && target <= current) {
      newErrors.targetSalary = 'Target salary must be higher than current/offered salary';
    }
    if (!roleTitle.trim()) {
      newErrors.roleTitle = 'Please enter a role title';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenerate = () => {
    if (!validate()) return;

    const current = parseFloat(currentSalary.replace(/,/g, ''));
    const target = parseFloat(targetSalary.replace(/,/g, ''));

    const result = generateScript(current, target, roleTitle.trim(), scenario, leverage, method);
    setScript(result);

    setTimeout(() => {
      document.getElementById('script-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSalaryInput = (
    value: string,
    setter: (val: string) => void
  ) => {
    const cleaned = value.replace(/[^0-9]/g, '');
    if (cleaned === '') {
      setter('');
      return;
    }
    const num = parseInt(cleaned, 10);
    setter(num.toLocaleString('en-US'));
  };

  const stepLabels = [
    { num: 1, title: 'Opening', key: 'opening' as const },
    { num: 2, title: 'The Ask', key: 'theAsk' as const },
    { num: 3, title: 'Justification', key: 'justification' as const },
    { num: 4, title: 'The Pause', key: 'thePause' as const },
  ];

  return (
    <>
      <ToolHero
        title="Salary Negotiation Script Generator"
        description="Get a word-for-word script customized to your situation. Enter your numbers and scenario, and we'll give you exactly what to say — plus responses for every pushback."
        category="Career Tools"
      />

      <main className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Input Form */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current / Offered Salary */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <DollarSign className="w-4 h-4 inline mr-1 -mt-0.5" />
                  Current or Offered Salary
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={currentSalary}
                    onChange={(e) => handleSalaryInput(e.target.value, setCurrentSalary)}
                    placeholder="85,000"
                    className={`w-full border ${
                      errors.currentSalary ? 'border-red-400' : 'border-gray-300'
                    } rounded-xl pl-8 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition`}
                  />
                </div>
                {errors.currentSalary && (
                  <p className="text-red-500 text-sm mt-1">{errors.currentSalary}</p>
                )}
              </div>

              {/* Target Salary */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Target className="w-4 h-4 inline mr-1 -mt-0.5" />
                  Your Target Salary
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={targetSalary}
                    onChange={(e) => handleSalaryInput(e.target.value, setTargetSalary)}
                    placeholder="100,000"
                    className={`w-full border ${
                      errors.targetSalary ? 'border-red-400' : 'border-gray-300'
                    } rounded-xl pl-8 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition`}
                  />
                </div>
                {errors.targetSalary && (
                  <p className="text-red-500 text-sm mt-1">{errors.targetSalary}</p>
                )}
              </div>

              {/* Role Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Briefcase className="w-4 h-4 inline mr-1 -mt-0.5" />
                  Role Title
                </label>
                <input
                  type="text"
                  value={roleTitle}
                  onChange={(e) => setRoleTitle(e.target.value)}
                  placeholder="e.g., Software Engineer"
                  className={`w-full border ${
                    errors.roleTitle ? 'border-red-400' : 'border-gray-300'
                  } rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition`}
                />
                {errors.roleTitle && (
                  <p className="text-red-500 text-sm mt-1">{errors.roleTitle}</p>
                )}
              </div>

              {/* Negotiation Scenario */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1 -mt-0.5" />
                  Negotiation Scenario
                </label>
                <div className="relative">
                  <select
                    value={scenario}
                    onChange={(e) => setScenario(e.target.value as Scenario)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition appearance-none bg-white"
                  >
                    <option value="new-offer">New Job Offer</option>
                    <option value="counter-offer">Counter Offer</option>
                    <option value="raise">Asking for a Raise</option>
                    <option value="promotion">Promotion Discussion</option>
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Communication Method */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {method === 'phone' ? (
                    <Phone className="w-4 h-4 inline mr-1 -mt-0.5" />
                  ) : method === 'email' ? (
                    <Mail className="w-4 h-4 inline mr-1 -mt-0.5" />
                  ) : (
                    <Users className="w-4 h-4 inline mr-1 -mt-0.5" />
                  )}
                  Communication Method
                </label>
                <div className="relative">
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value as Method)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition appearance-none bg-white"
                  >
                    <option value="phone">Phone Call</option>
                    <option value="email">Email</option>
                    <option value="in-person">In-Person Meeting</option>
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Leverage */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Award className="w-4 h-4 inline mr-1 -mt-0.5" />
                  Your Leverage{' '}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={leverage}
                  onChange={(e) => setLeverage(e.target.value)}
                  placeholder="e.g., competing offer, unique skills, recent achievement"
                  rows={3}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none"
                />
              </div>
            </div>

            {/* Generate Button */}
            <div className="mt-8 text-center">
              <button
                onClick={handleGenerate}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3.5 px-10 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg"
              >
                <MessageSquare className="w-5 h-5" />
                Generate Script
              </button>
            </div>
          </div>

          {/* Results */}
          {script && (
            <div id="script-results">
              {/* Header + Copy */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Your Negotiation Script
                </h2>
                <CopyButton
                  text={script.fullText}
                  label="Copy Full Script"
                  className="!px-4 !py-2 !text-base"
                />
              </div>

              {/* Step-by-step script cards */}
              <div className="space-y-4 mb-10">
                {stepLabels.map((step) => (
                  <div
                    key={step.key}
                    className="bg-white rounded-xl border border-gray-200 p-6"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                        {step.num}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">
                          {step.title}
                        </h3>
                        <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                          {script[step.key]}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pushback Section */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  If They Say...
                </h2>
                <p className="text-gray-500 mb-6">
                  Prepared responses for the 5 most common pushbacks
                </p>
                <div className="space-y-3">
                  {script.pushbacks.map((pb, i) => (
                    <div
                      key={i}
                      className="border-l-4 border-amber-500 bg-amber-50 rounded-r-xl p-4"
                    >
                      <p className="font-semibold text-gray-900 mb-2">
                        {pb.objection}
                      </p>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {pb.response}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Alternative Negotiation Points */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Alternative Negotiation Points
                </h2>
                <p className="text-gray-500 mb-6">
                  If base salary is firm, negotiate these instead
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {script.alternatives.map((alt, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        {alternativeIcons[alt.icon]}
                        <h3 className="font-bold text-gray-900">{alt.label}</h3>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {alt.suggestion}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Negotiation Education */}
              <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">The Research Behind Salary Negotiation</h3>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Studies consistently show that candidates who negotiate their initial offer earn significantly more over their careers. According to research published by the Society for Human Resource Management (SHRM), failing to negotiate a starting salary can cost a worker over $600,000 in lost earnings across a 40-year career due to the compounding effect of a lower baseline.
                  </p>
                  <p>
                    The most effective negotiators use <strong>anchoring</strong>, a well-documented cognitive bias where the first number mentioned in a negotiation heavily influences the final outcome. By stating a well-researched target salary early, you set the reference point for the entire conversation. The Bureau of Labor Statistics Occupational Employment and Wage Statistics (OES) program is an authoritative source for understanding what roles actually pay, broken down by region and percentile.
                  </p>
                  <p>
                    Beyond base salary, total compensation includes bonuses, equity, signing bonuses, relocation assistance, flexible work arrangements, professional development budgets, and additional PTO. Harvard Business Review research suggests that negotiating on multiple dimensions simultaneously -- rather than just salary -- leads to better outcomes for both parties because it creates opportunities for mutually beneficial trade-offs.
                  </p>
                  <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                    <strong>Sources &amp; Further Reading:</strong>{' '}
                    <a href="https://www.bls.gov/oes/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">BLS Occupational Employment &amp; Wage Statistics</a>
                    {' · '}
                    <a href="https://www.shrm.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">SHRM - Society for Human Resource Management</a>
                    {' · '}
                    <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review - Career Planning</a>
                  </p>
                </div>
              </section>

              {/* CTA */}
              <ToolCTA
                headline="Negotiate from a position of strength"
                description="A powerful resume gives you more leverage at the negotiation table. Build yours in minutes with Superpower Resume."
                buttonText="Build My Resume Free"
              />
            </div>
          )}
        </div>
      </main>
    </>
  );
}
